import { ChevronRight } from '@orgus/ui/icons';
import type { LucideIcon } from '@orgus/ui/icons';
import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Button, cn } from '@orgus/ui';
import type { SignInNotice, SignInNoticeKind, SignInNoticeTone } from './signinStates.ts';

/**
 * The states that stop sign-in altogether. They take the form side for themselves instead of
 * sitting above methods that cannot work: one icon, the status label, what happened as the
 * heading, what to do next, the one action that helps, and why it happened one click away.
 */
export const BLOCKING_KINDS: ReadonlySet<SignInNoticeKind> = new Set(['offline', 'unreachable', 'access-denied']);

const TILE: Record<SignInNoticeTone, string> = {
  failed: 'border-failed/30 bg-failed-soft text-failed',
  offline: 'border-offline/30 bg-offline-soft text-offline',
  neutral: 'border-border bg-surface text-fg-muted',
};
const LABEL: Record<SignInNoticeTone, string> = { failed: 'text-failed', offline: 'text-offline', neutral: 'text-fg-muted' };

export interface SignInStateScreenProps {
  id?: string;
  notice: SignInNotice;
  icon: LucideIcon;
  /** The action that helps (Try again, Back to sign-in), with anything that belongs under it. */
  children?: ReactNode;
  /** Commands in the words are set in mono (withCommands). */
  words: (text: string) => ReactNode;
  /** Take focus once: an error already there when the page opens is not announced by every screen reader. */
  focusOnMount?: boolean;
}

export function SignInStateScreen({ id, notice, icon: Icon, children, words, focusOnMount = false }: SignInStateScreenProps) {
  const ref = useRef<HTMLElement>(null);
  const [why, setWhy] = useState(false);
  useEffect(() => {
    if (focusOnMount) ref.current?.focus();
  }, [focusOnMount]);
  return (
    <section
      ref={ref}
      id={id}
      tabIndex={-1}
      role={notice.urgent ? 'alert' : 'status'}
      data-notice={notice.kind}
      data-state-screen={notice.kind}
      aria-labelledby={id ? `${id}-title` : undefined}
      className="flex flex-col gap-6 outline-none"
    >
      <div aria-hidden className={cn('flex size-10 items-center justify-center rounded-card border', TILE[notice.tone])}>
        <Icon className="size-5" />
      </div>
      <div className="flex flex-col gap-2">
        <p className={cn('text-sm font-medium', LABEL[notice.tone])}>{notice.label}</p>
        <h1 id={id ? `${id}-title` : undefined} className="text-2xl font-semibold text-fg">
          {words(notice.what)}
        </h1>
        {notice.next && <p className="text-md text-fg-muted">{words(notice.next)}</p>}
      </div>
      {children}
      {notice.why && (
        <div className="flex flex-col gap-2">
          <Button
            variant="ghost"
            size="sm"
            icon={<ChevronRight className={cn('transition-transform duration-(--duration-fast)', why && 'rotate-90')} />}
            aria-expanded={why}
            onClick={() => setWhy((v) => !v)}
            className="-ml-2 self-start text-fg-subtle"
          >
            Why this happened
          </Button>
          {why && <p className="text-sm text-fg-muted">{words(notice.why)}</p>}
        </div>
      )}
    </section>
  );
}
